import AgentCard from "@/components/molecules/AgentCard";
import { Skeleton } from "@/components/ui/skeleton";
import { IAgentData } from "@/interfaces/IAgentData";
import { cn } from "@/lib/utils";
import { FC, HTMLAttributes, memo } from "react";
import Nodata from "../molecules/Nodata";

interface IAgentListProps {
  className?: string;
  dataAgents: IAgentData[];
  isDataAgentsLoading: boolean;
}

const SkeletonAgentCard: FC<HTMLAttributes<HTMLDivElement>> = memo(() => {
  return (
    <div className="flex-1 overflow-hidden flex flex-col gap-2 w-full lg:w-[282px]">
      <Skeleton className="aspect-square rounded-[8px] overflow-hidden bg-base-secondary/10" />
      <div className="px-2 py-4 flex flex-col items-start gap-4 w-full">
        <div className="flex flex-col gap-4 w-full">
          <Skeleton className="rounded-[8px] overflow-hidden bg-base-secondary/10 h-[20px] w-full" />
          <div className="flex flex-col gap-2">
            <Skeleton className="rounded-[4px] overflow-hidden bg-base-secondary/10 h-[10px] w-full" />
            <Skeleton className="rounded-[4px] overflow-hidden bg-base-secondary/10 h-[10px] w-full" />
          </div>
          <div className="flex justify-start items-center w-full h-auto gap-2 ">
            <div className="flex-1 flex items-center gap-2">
              <Skeleton className="rounded-full overflow-hidden bg-base-secondary/10 w-[20px] h-[20px]" />
              <Skeleton className="rounded-[4px] overflow-hidden bg-base-secondary/10 h-[10px] flex-1" />
            </div>
            <div className="flex-1 flex items-center gap-2">
              <Skeleton className="rounded-full overflow-hidden bg-base-secondary/10 w-[20px] h-[20px]" />
              <Skeleton className="rounded-[4px] overflow-hidden bg-base-secondary/10 h-[10px] flex-1" />
            </div>
          </div>
          <div className="flex justify-start items-center w-full h-auto gap-2 ">
            <div className="flex-1">
              <Skeleton className="rounded-full overflow-hidden bg-base-secondary/10 h-[14px] flex-1" />
            </div>
            <div className="flex-1">
              <Skeleton className="rounded-full overflow-hidden bg-base-secondary/10 h-[14px] flex-1" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

SkeletonAgentCard.displayName = "SkeletonAgentCard";

export default function AgentList({
  className,
  dataAgents,
  isDataAgentsLoading,
}: IAgentListProps) {
  // If data is loading, return loading spinner
  if (isDataAgentsLoading)
    return (
      <div
        className={cn(
          "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-8",
          className,
        )}
      >
        {[...Array(4)].map((_, i) => (
          <SkeletonAgentCard key={i} />
        ))}
      </div>
    );

  // If there is no data, return empty div
  if (!dataAgents) return <div>No data</div>;

  if (dataAgents.length <= 0) return <Nodata />;

  return (
    <>
      <div
        className={cn(
          "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-8",
          className,
        )}
      >
        {dataAgents.map((e) => (
          <AgentCard key={e._id} {...e} />
        ))}
      </div>
    </>
  );
}
